import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, FileSpreadsheet, Loader2, Lightbulb, TrendingUp, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { api, formatApiError } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import ScopeFilters from "../../components/stats/ScopeFilters";
import { Card, Kpi, pct, PeriodInfo, RatingChart, SentimentBars, MonthlyChart, GroupTable, Issues, Delays } from "../../components/stats/StatsParts";
import { fmtDate } from "../../lib/labels";
import { useCompany } from "../../components/layout/CompanyLayout";
import { InsightResult } from "../../components/insights/AiResult";

const PERIODS = [["week", "Hebdomadaire"], ["month", "Mensuel"], ["quarter", "Trimestriel"], ["year", "Annuel"]];
const today = () => new Date().toISOString().slice(0, 10);
const delta = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${v}`);

const Comparisons = ({ rows }) => (
  <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="report-comparisons">
    <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="py-2">Indicateur</th><th>Période</th><th>Précédente</th><th>Écart</th></tr></thead>
    <tbody className="divide-y">{rows.map((c) => <tr key={c.key}><td className="py-2">{c.label}</td><td className="font-medium">{c.current ?? "—"}</td><td>{c.previous ?? "—"}</td><td>{delta(c.delta)}</td></tr>)}</tbody>
  </table></div>
);

const Bullets = ({ items, testId }) => <ul className="list-disc space-y-1.5 pl-5 text-sm text-slate-700" data-testid={testId}>{items.map((t) => <li key={t}>{t}</li>)}</ul>;

const Report = ({ r }) => {
  const d = r.current;
  return (
    <div className="space-y-6" data-testid="report-view">
      <PeriodInfo data={d} testId="report-period" />
      <p className="text-xs text-slate-500">Comparaison avec le {fmtDate(r.previous.period.from, false)} – {fmtDate(r.previous.period.to, false)} ({r.previous.analyzed} avis).</p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi testId="rep-kpi-total" label="Avis analysés" value={d.analyzed} />
        <Kpi testId="rep-kpi-avg" label="Note moyenne" value={d.avg_rating} />
        <Kpi testId="rep-kpi-response" label="Taux de réponse" value={pct(d.response_rate)} />
        <Kpi testId="rep-kpi-resolution" label="Taux de résolution" value={pct(d.resolution_rate)} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Comparaison avec la période précédente" testId="rep-compare"><Comparisons rows={r.comparisons} /></Card>
        <Card title={<span className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-600" />Tendances</span>} testId="rep-trends"><Bullets items={r.trends} testId="report-trends" /></Card>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Répartition des notes"><RatingChart byRating={d.by_rating} /></Card>
        <Card title="Positifs, neutres, négatifs"><SentimentBars sentiment={d.sentiment} /></Card>
        <Card title="Délais moyens"><Delays delays={d.delays} /></Card>
      </div>
      {d.monthly.length > 1 && <Card title="Évolution mensuelle"><MonthlyChart monthly={d.monthly} /></Card>}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Par agence"><GroupTable rows={d.by_agency} label="Agence" testId="rep-agencies" /></Card>
        <Card title="Par service"><GroupTable rows={d.by_service} label="Service" testId="rep-services" /></Card>
        <Card title="Problèmes récurrents"><Issues items={d.recurring_issues} /></Card>
      </div>
      <Card title={<span className="flex items-center gap-2"><Lightbulb className="h-4 w-4 text-amber-500" />Pistes d'amélioration (règles automatiques)</span>} testId="rep-suggestions"><Bullets items={r.suggestions} testId="report-suggestions" /></Card>
      {r.sector && <SectorReport s={r.sector} />}
      {r.ai && <Card title="Analyse intelligente (IA) la plus récente couvrant la période" testId="rep-ai"><InsightResult ins={r.ai} readOnly /></Card>}
    </div>
  );
};

const Deliveries = () => {
  const { data } = useApi("/company/reports/deliveries");
  if (!data?.length) return <p className="mt-8 flex items-center gap-2 text-xs text-slate-500"><Mail className="h-3.5 w-3.5" />Le rapport mensuel est envoyé automatiquement le 1er de chaque mois aux propriétaires et responsables.</p>;
  return (
    <Card title="Envois automatiques" testId="report-deliveries" className="mt-8">
      <ul className="space-y-1 text-sm text-slate-600">{data.map((x) => <li key={x.key}>{fmtDate(x.sent_at)} · période {fmtDate(x.from, false)} – {fmtDate(x.to, false)} · {x.analyzed} avis · {x.recipients} destinataire(s)</li>)}</ul>
    </Card>
  );
};

const SectorReport = ({ s }) => (
  <div className="grid gap-6 lg:grid-cols-2" data-testid="rep-sector">
    {s.report_sections.includes("criteria") && <Card title={`Critères${s.profile.name ? ` — ${s.profile.name}` : ""}`} testId="rep-criteria">
      <ul className="divide-y divide-slate-100 text-sm">{s.criteria.map((c) => <li key={c.code} className="flex justify-between py-1.5" data-testid={`rep-criterion-${c.code}`}><span>{c.label}{c.common ? "" : <span className="ml-1 text-xs text-indigo-700">secteur</span>}</span><b>{c.avg ?? "—"}{c.avg ? "/5" : ""} <span className="font-normal text-slate-400">({c.count})</span></b></li>)}</ul>
    </Card>}
    {s.report_sections.includes("sla") && <Card title="Délais cibles par priorité" testId="rep-sla">
      <ul className="divide-y divide-slate-100 text-sm">{s.sla.map((x) => <li key={x.priority} className="py-1.5" data-testid={`rep-sla-${x.priority}`}><b>{x.label || x.priority}</b> · {x.count} avis · 1re réponse ≤ {x.target_first_response_hours} h : {x.first_response_ok}/{x.first_response_measured} · résolution ≤ {x.target_resolution_hours} h : {x.resolution_ok}/{x.resolution_measured}</li>)}</ul>
    </Card>}
  </div>
);

export default function Reports() {
  const [params] = useSearchParams();
  const [period, setPeriod] = useState(params.get("period") || "month");
  const [ref, setRef] = useState(params.get("ref") || today());
  const [scope, setScope] = useState({ agency_id: "", service_id: "", category: "" });
  const [busy, setBusy] = useState("");
  const { can } = useCompany();
  const agencies = useApi("/company/agencies");
  const services = useApi("/company/services");
  const qs = new URLSearchParams(Object.entries({ period, ref, ...scope }).filter(([, v]) => v)).toString();
  const { data, loading, error, reload } = useApi(`/company/reports?${qs}`);
  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get(`/company/reports/export?${qs}&format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `voxa-afrika-rapport-${period}-${data?.current.period.from || ref}.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  return (
    <div>
      <PageHeader title="Rapports" description="Rapports périodiques calculés sur les avis réellement soumis, avec comparaison à la période précédente."
        actions={can("reports:export") && <div className="flex gap-2">
          <Button variant="outline" onClick={() => download("pdf")} disabled={!!busy} data-testid="report-export-pdf">{busy === "pdf" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}PDF</Button>
          <Button variant="outline" onClick={() => download("xlsx")} disabled={!!busy} data-testid="report-export-xlsx">{busy === "xlsx" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1.5 h-4 w-4" />}Excel</Button>
        </div>} />
      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        <div className="flex gap-1 rounded-full bg-slate-100 p-1" data-testid="report-period-tabs">
          {PERIODS.map(([k, l]) => <button key={k} onClick={() => setPeriod(k)} data-testid={`report-period-${k}`}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors ${period === k ? "bg-white font-medium shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>)}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-500">Période contenant le<Input type="date" value={ref} onChange={(e) => setRef(e.target.value || today())} className="w-44" data-testid="report-ref-date" /></label>
      </div>
      <ScopeFilters value={scope} onChange={setScope} agencies={agencies.data} services={services.data} withDates={false} />
      {loading && !data && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && <div className={loading ? "opacity-60" : ""}><Report r={data} /></div>}
      <Deliveries />
    </div>
  );
}
